import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Services from './pages/Services';
import EduCenter from './pages/EduCenter';
import CourseDetail from './pages/CourseDetail';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import LearnPage from './pages/LearnPage';
import { Login, Register } from './pages/Auth';
import SplashScreen from './components/SplashScreen';

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div className="spinner" />
    </div>
  );
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

function Layout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-0RW5W0P7PV', { page_path: location.pathname + location.search });
    }
  }, [location]);

  return (
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/about" element={<Layout><About /></Layout>} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />
      <Route path="/services" element={<Layout><Services /></Layout>} />
      <Route path="/educenter" element={<Layout><EduCenter /></Layout>} />
      <Route path="/educenter/:id" element={<Layout><CourseDetail /></Layout>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout><Profile /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/learn/:id" element={
        <ProtectedRoute>
          <Layout><LearnPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute role="admin">
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={
        <Layout>
          <div style={{ textAlign: 'center', padding: '8rem 1rem', paddingTop: 'calc(68px + 6rem)' }}>
            <h1 style={{ fontSize: 80, fontWeight: 800, color: 'var(--ac-cyan)', marginBottom: '1rem' }}>404</h1>
            <p style={{ color: 'var(--ac-muted)', fontSize: 18, marginBottom: '2rem' }}>Page not found.</p>
            <a href="/" className="btn btn-primary btn-lg">Back to Home</a>
          </div>
        </Layout>
      } />
    </Routes>
  );
}

export default function App() {
  // Show splash once per browser session
  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem('splash_done')
  );

  const handleSplashDone = () => {
    sessionStorage.setItem('splash_done', '1');
    setShowSplash(false);
  };

  return (
    <BrowserRouter>
      {showSplash && <SplashScreen onDone={handleSplashDone} />}
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'var(--ac-card2)',
              color: 'var(--ac-white)',
              border: '1px solid var(--ac-border)',
              borderRadius: '10px',
            },
            success: { iconTheme: { primary: '#00dc82', secondary: '#060d1f' } },
            error: { iconTheme: { primary: '#ff4757', secondary: '#060d1f' } },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
